import { FormCtx, UniversalFieldConfig } from "./types";

function resolveLabel<T>(field: UniversalFieldConfig<T>, ctx: FormCtx<T>): string {
  if (typeof field.label === "function") return field.label(ctx);
  return field.label || field.name;
}

function resolveRequired<T>(field: UniversalFieldConfig<T>, ctx: FormCtx<T>): boolean {
  if (typeof field.required === "function") return field.required(ctx);
  return !!field.required;
}

function isEmpty(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

export function validateFields<T extends Record<string, any>>(
  fields: UniversalFieldConfig<T>[],
  ctx: FormCtx<T>
): { valid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};

  fields.forEach((field) => {
    const errorKey = field.errorKey || field.name;
    const value = ctx.values[field.name];

    if (resolveRequired(field, ctx) && isEmpty(value)) {
      const message =
        typeof field.requiredMessage === "function"
          ? field.requiredMessage(ctx)
          : field.requiredMessage;
      errors[errorKey] = message || `${resolveLabel(field, ctx)} is required`;
      return;
    }

    if (field.validate) {
      const message = field.validate(value, ctx);
      if (message) {
        errors[errorKey] = message;
      }
    }
  });

  return { valid: Object.keys(errors).length === 0, errors };
}

export function getMissingRequiredLabels<T extends Record<string, any>>(
  fields: UniversalFieldConfig<T>[],
  ctx: FormCtx<T>
): string[] {
  return fields
    .filter((field) => resolveRequired(field, ctx) && isEmpty(ctx.values[field.name]))
    .map((field) => resolveLabel(field, ctx));
}

export function useEntityFormValidation<T extends Record<string, any>>(
  fields: UniversalFieldConfig<T>[],
  ctx: FormCtx<T>
) {
  const validate = () => validateFields(fields, ctx);
  const isValid = () => validateFields(fields, ctx).valid;
  const getMissingLabels = () => getMissingRequiredLabels(fields, ctx);

  return { validate, isValid, getMissingLabels };
}
